import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Avatar, Box, Grid, makeStyles } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import clsx from 'clsx'
import ChatAgentIcon from 'components/RightPane/ChatAgentIcon'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React, { forwardRef } from 'react'
import { useChatProfileStore } from './ChatProfileStore'
import MessageActions from './MessageActions'
import MessageContent from './MessageContent'
import { chatAvatarColor } from './colors'
import { Message } from './message'
import { useChatBot } from './useChatBotHook'
import { useChatMessageStore } from './useChatMessageStore'
interface MessageChatStyle {
    isOnHeader?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}
const useStyles = makeStyles((theme) => ({
    messageContainer: {
        width: '100%',
        display: 'flex',
        wrap: 'nowrap',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    suggestedMessageContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        cursor: 'pointer',
    },
    avatar: {
        height: '32px',
        width: '32px',
        backgroundColor: chatAvatarColor,
        fontFamily: 'Roboto',
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
    },
    avatarContainer: {
        minWidth: '32px',
        width: '32px',
    },
    messageContent: {
        maxWidth: (props: MessageChatStyle) =>
            pxToRem(props.isOnHeader ? 378 : 288),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    messageContentSkeleton: {
        width: '100%',
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    messageActions: (props: MessageChatStyle) => ({
        height: '24px',
        width: '100%',
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? '#EDF0F6'
            : theme.palette.common.white,
        marginTop: theme.spacing(1),
        transition: 'display 1s ease-in-out',
    }),
    agentSkeleton: {
        borderRadius: '0px 8px 8px 8px',
    },
    agentActionsSkeleton: {
        marginTop: theme.spacing(1),
    },
    activeHoverTooltip: {
        visibility: 'visible',
    },
    inactiveHoverTooltip: {
        visibility: 'hidden',
    },
}))

export interface MessageContainerProps {
    message: Message
    tabIndex?: number
    ref?: React.Ref<HTMLDivElement>
    isOnHeader?: boolean
    isFirstSuggested?: boolean
}

const MessageContainer: React.FC<MessageContainerProps> = forwardRef(
    ({ message, tabIndex, isOnHeader, isFirstSuggested }, ref) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
        const classes = useStyles({
            isOnHeader,
            isBetaWorkspaceToggleEnabled,
        })
        const { askSuggestedMessage } = useChatBot()
        const chatUser = useChatProfileStore((state) => state.chatUser)
        const isLoading = useChatMessageStore((state) => state.isLoading)

        const [isMessageFocused, setMessageFocused] = React.useState(false)

        const messageContentRef = React.useRef<HTMLDivElement>(null)

        const handleMessageHovered = React.useCallback(() => {
            setMessageFocused(true)
        }, [])

        const handleMessageBlur = React.useCallback(() => {
            setMessageFocused(false)
        }, [])

        const handleMessageClicked = React.useCallback(() => {
            // We can't ask while the bot is loading
            if (isLoading) {
                return
            }
            askSuggestedMessage(message)
        }, [isLoading, message, askSuggestedMessage])

        const isAvatarVisible = React.useMemo(() => {
            return message.role === 'agent' || message.role === 'user'
        }, [message])

        const areActionsVisible = React.useMemo(() => {
            return (
                isMessageFocused &&
                message.role === 'agent' &&
                !message.isGreeting &&
                !message.isError
            )
        }, [message, isMessageFocused])

        const userInitials: string = React.useMemo(() => {
            return `${chatUser?.profileInfo?.firstName?.[0]}${chatUser?.profileInfo?.lastName?.[0]}`.toUpperCase()
        }, [chatUser])

        const avatarSrc = React.useMemo(
            () =>
                message.role === 'user' ? chatUser?.profileInfo?.imageUrl : '',
            [message, chatUser]
        )

        //#region Accessibility

        /** Used when navigating through the keyboard for accessibility to indicate if actions are available */
        const [isActionable, setIsActionable] = React.useState(false)
        const actionsRef = React.useRef<HTMLButtonElement>(null)

        /** Focusing on a message will display its actions (like, dislike, regenerate, etc) and keyboard navigation for actions */
        const enterFocusOnMessage = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                event.stopPropagation()
                event.preventDefault()

                // For accessibility, when the user presses enter on a suggested message, ask it
                handleMessageClicked()

                // For accessibility, focus on the message and display actions
                setIsActionable(true)
                actionsRef?.current?.focus()
            },
            [handleMessageClicked]
        )

        /** Removes focus on the message and bring keyboard navigation out to its parent container */
        const escapeFocusOnMessage = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                event.stopPropagation()
                event.preventDefault()

                // For accessibility, hide the actions
                setIsActionable(false)
                actionsRef?.current?.blur()
            },
            []
        )

        const handleMessageKeyDown = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter') {
                    enterFocusOnMessage(event)
                }

                if (event.key === 'Escape') {
                    escapeFocusOnMessage(event)
                }
            },
            [enterFocusOnMessage, escapeFocusOnMessage]
        )

        //#endregion

        return (
            <Grid
                container
                wrap="nowrap"
                direction={message.role === 'agent' ? 'row' : 'row-reverse'}
                className={clsx(classes.messageContainer, {
                    [classes.suggestedMessageContainer]:
                        message.role === 'suggested',
                })}
                tabIndex={tabIndex}
                onKeyDown={handleMessageKeyDown}
                onFocus={handleMessageHovered}
                ref={ref}
                data-testid="messageContainer"
            >
                {message.isPlaceholder ? (
                    <>
                        <Grid
                            item
                            className={classes.avatarContainer}
                            data-testid="messageContainerSkeleton"
                        >
                            <Skeleton variant="circle" width={32} height={32} />
                        </Grid>
                        <Grid item className={classes.messageContentSkeleton}>
                            <Skeleton
                                variant="rect"
                                height={64}
                                className={classes.agentSkeleton}
                            />
                            <Skeleton
                                variant="rect"
                                height={16}
                                className={classes.agentActionsSkeleton}
                            />
                        </Grid>
                    </>
                ) : (
                    <>
                        {isAvatarVisible && (
                            <Grid item className={classes.avatarContainer}>
                                <Avatar
                                    className={classes.avatar}
                                    color="red"
                                    src={avatarSrc}
                                    alt={`${message.role} avatar`}
                                    aria-label={`${message.role} avatar`}
                                    role="img"
                                >
                                    {message.role === 'agent' ? (
                                        <ChatAgentIcon />
                                    ) : (
                                        userInitials
                                    )}
                                </Avatar>
                            </Grid>
                        )}
                        <Grid
                            item
                            className={classes.messageContent}
                            onMouseOver={handleMessageHovered}
                            onMouseLeave={handleMessageBlur}
                            onClick={handleMessageClicked}
                            data-testid="messageContentContainer"
                        >
                            <MessageContent
                                ref={messageContentRef}
                                message={message}
                                isFirstSuggested={isFirstSuggested}
                            />

                            {areActionsVisible && (
                                <Box
                                    component="div"
                                    className={classes.messageActions}
                                    data-testid="messageActions"
                                >
                                    <MessageActions
                                        message={message}
                                        tabIndex={isActionable ? 0 : -1}
                                        ref={actionsRef}
                                        messageContentRef={messageContentRef}
                                    />
                                </Box>
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
        )
    }
)

export default MessageContainer
